/**
 * useScoreSelection
 *
 * Selektions-Teil der interaktiven Vorschau: Klick-Behandlung (inkl. **Mehrfachauswahl** per
 * Ctrl/Cmd-Klick = umschalten, Shift-Klick = Bereich, Ctrl-/Shift-Klick auf den freien Taktbereich
 * = ganzer Takt – ohne Auswahl-Modifier passiert dort nichts, nur der Doppelklick öffnet weiterhin
 * das Akkord-Panel),
 * Takt-/Noten-Auswahl inkl. Durchsteppen (selectNextNote/-Measure, selectPreviousNote/-Measure),
 * Öffnen der Edit-Panels (Note/Akkord/Tonart) und Wiederherstellen der Markierung nach jedem
 * Re-Render. `selectedNotes` hält die geordnete Auswahl (dedupliziert per {@link noteKey});
 * `selectedNote` ist die **primäre** (zuletzt geklickte) Note – sie treibt Cursor, Panel-Position
 * und die Akkord-/Tonart-Panels, sodass die bestehende Einzelauswahl (Auswahl der Größe 1)
 * unverändert funktioniert. `selectedMeasureIndex` ist die interne eindeutige Taktidentität;
 * `selectedMeasure` bleibt nur die gedruckte Nummer für den Panel-Kontext. Ein
 * watch(selectedMeasureIndex) positioniert bei reiner Takt-Navigation eine Note; er ist gegen die
 * Mehrfachauswahl abgesichert (er greift nur, wenn die primäre Note noch nicht im Takt liegt).
 *
 * Beide Klick-Handler protokollieren zusätzlich in `actionScope`, **wohin** gezeigt wurde (Note vs.
 * freie Taktfläche) – daraus baut das `NoteActionsOverlay` sein Menü. Genau dieser Watch ist der
 * Grund, warum der Bereich nicht aus `selectedNote` ableitbar ist; die Herleitung steht am Typ
 * {@link @/components/musicxml-preview/noteActionsMenu!ActionScope}.
 *
 * Sub-Composable von {@link @/composables/useOsmdInteractiveScore}.
 */
import {type Ref, ref, shallowRef, watch} from 'vue';
import {GraphicalNote, OpenSheetMusicDisplay} from 'opensheetmusicdisplay';
import type {BarlineSide, KeySignatureInfo, NoteInfo, TimeSignatureInfo} from '@/composables/musicXmlDom';
import {
    getChordFromMeasure,
    getKeySignatureFromMeasure,
    getTimeSignatureFromMeasure,
    noteKey,
    pitchToMidi,
} from '@/composables/musicXmlDom';
import type {NotePreviewTone} from '@/composables/osmd/notePreviewTone';
import {getNoteInfoFromGraphicalNote} from './graphicalNoteInfo';
import {computeFullMeasures} from './selectionLogic';
import type {EditTab} from '@/components/musicxml-preview/editContext';
import type {ActionScope} from '@/components/musicxml-preview/noteActionsMenu';
import type {ScoreHitTesting} from './useScoreHitTesting';
import type {ScoreHighlight} from './useScoreHighlight';


/** Ergebnis eines Rechtsklicks auf die Partitur – es bestimmt den Geltungsbereich des
 *  Aktionsmenüs. `null` = außerhalb **jedes** Takts; nur dann darf der Host das native
 *  Browser-Kontextmenü durchlassen. */
export type ScoreContextMenuHit =
    | { scope: 'note'; note: NoteInfo }
    | { scope: 'measure'; measureIndex: number };

/** Ein Treffer der Rubber-Band-Auswahl: `NoteInfo` (Identität/XML-Adresse) plus die konkrete
 *  `GraphicalNote`-Instanz (Highlight färbt exakt den getroffenen Notenkopf). */
export interface DragSelectionHit {
    info: NoteInfo;
    gNote: GraphicalNote;
}

export interface ScoreSelectionDeps {
    getOsmd: () => OpenSheetMusicDisplay | null;
    musicXml: Ref<string | null>;
    hitTesting: ScoreHitTesting;
    highlight: ScoreHighlight;
    /** Player-Status: im Leerlauf wird der Note-Cursor versteckt, während der Wiedergabe nicht. */
    isPlaying: Ref<boolean>;
    // ---- Selektions-State (Eigentümer: Orchestrator, geteilt mit den Edit-Aktionen) ----
    /** Interne eindeutige Taktidentität; `selectedMeasure` bleibt nur gedruckter Editor-Wert. */
    selectedMeasureIndex: Ref<number | null>;
    selectedMeasure: Ref<number | null>;
    clickedGraphicalNote: Ref<GraphicalNote | null>;
    selectedNote: Ref<NoteInfo | null>;
    /** Geordnete Mehrfachauswahl (dedupliziert per {@link noteKey}); enthält die primäre Note. */
    selectedNotes: Ref<NoteInfo[]>;
    /** Interne ordinale Indizes der vollständig ausgewählten Takte. */
    selectedMeasureIndices: Ref<number[]>;
    /** Per Ganztakt-Auswahl gewählte Takte (für Anzeige/Batch-Kontext); primärer Takt bleibt
     *  `selectedMeasure`. */
    selectedMeasures: Ref<number[]>;
    currentMeasureChord: Ref<string | null>;
    currentKeySignature: Ref<KeySignatureInfo | null>;
    currentTimeSignature: Ref<TimeSignatureInfo | null>;
    activeEditTab: Ref<EditTab>;
    /** Geltungsbereich des Aktionsmenüs = WO zuletzt auf die Partitur gezeigt wurde. Geschrieben
     *  ausschließlich von {@link useScoreSelection.handleContainerClick} und
     *  {@link useScoreSelection.handleContainerContextMenu}; **nicht ableitbar**, die Begründung
     *  steht am Typ in `musicxml-preview/noteActionsMenu.ts`. */
    actionScope: Ref<ActionScope>;
    /** Vom Kanten-Doppelklick vorgewählte Taktseite des Struktur-Editors (sonst `null`). */
    structureSide: Ref<BarlineSide | null>;
    /** Einmal-Unterdrückung der Auto-Notenwahl des Takt-Watches: `insertMeasure('after')` setzt
     *  den Takt-Kontext VOR dem Re-Render – die Auto-Wahl liefe gegen das alte Notenbild
     *  (falsche Note, Vorschau-Ton, am Stückende Kontext-Rücksetzer). Der Watch konsumiert das
     *  Flag (setzt es zurück). */
    suppressMeasureAutoSelect: Ref<boolean>;
    selectedNoteX: Ref<number | null>;
    selectedNoteY: Ref<number | null>;
    /** Lässt die ausgewählte Note hören (no-op in der Griffschrift-Ausgabe). */
    previewTone: NotePreviewTone;
}

interface SelectionHighlightSnapshot {
    selectedNotes: NoteInfo[];
    selectedNote: NoteInfo | null;
    clickedGraphicalNote: GraphicalNote | null;
    selectedMeasureIndex: number | null;
}

/** Grafische Noten der aktuellen Auswahl (für das rote Multi-Highlight). */
function graphicalNotesForSelection(
    hitTesting: ScoreHitTesting,
    snapshot: SelectionHighlightSnapshot,
): GraphicalNote[] {
    const {selectedNotes, selectedNote, clickedGraphicalNote} = snapshot;
    const out: GraphicalNote[] = [];
    for (const info of selectedNotes) {
        // Beim Primärtreffer exakt die angeklickte OSMD-Instanz verwenden. Das vermeidet, dass
        // ein erneuter Lookup bei Akkorden/überlappenden Systemen einen anderen Notenkopf färbt.
        if (clickedGraphicalNote && selectedNote && noteKey(info) === noteKey(selectedNote)) {
            out.push(clickedGraphicalNote);
            continue;
        }
        if (info.measureIndex === undefined) continue;
        const g = hitTesting.findGraphicalNoteByMeasureIndex(info.measureIndex, info.noteIndex, info.staffIndex);
        if (g) out.push(g);
    }
    return out;
}

/** Rendert einen Selection-Snapshot (Auswahl-Layer + Takt-Cursor cursors[1] + grüner Note-Cursor),
 *  ohne den zugrunde liegenden Selektions-State zu verändern. */
function renderSelectionHighlight(
    hitTesting: ScoreHitTesting,
    highlight: ScoreHighlight,
    snapshot: SelectionHighlightSnapshot,
): void {
    if (snapshot.selectedMeasureIndex !== null) {
        const gMeasure = hitTesting.findGraphicalMeasureByIndex(
            snapshot.selectedMeasureIndex,
            snapshot.selectedNote?.staffIndex,
        );
        if (gMeasure) highlight.highlightMeasure(gMeasure);
    }
    highlight.highlightNotes(
        graphicalNotesForSelection(hitTesting, snapshot),
        snapshot.clickedGraphicalNote,
    );
}

/** Rendert eine transiente Hover-Vorschau: Hover-Takt-Cursor (cursors[2]) + Hover-Farb-Layer über
 *  der unveränderten Auswahl-Färbung. Berührt weder Auswahl-Layer noch grünen Note-Cursor
 *  (kritisch – nicht auf `highlightNotes` rückbauen: das entfärbte die gesamte Auswahl). */
function renderHoverPreview(
    hitTesting: ScoreHitTesting,
    highlight: ScoreHighlight,
    snapshot: SelectionHighlightSnapshot,
): void {
    if (snapshot.selectedMeasureIndex !== null) {
        const gMeasure = hitTesting.findGraphicalMeasureByIndex(
            snapshot.selectedMeasureIndex,
            snapshot.selectedNote?.staffIndex,
        );
        if (gMeasure) highlight.highlightMeasure(gMeasure, true);
    }
    highlight.highlightHoverNotes(graphicalNotesForSelection(hitTesting, snapshot));
}

/** Takt- und Noten-Highlight des tatsächlich ausgewählten Zustands neu setzen. */
function refreshSelectionHighlight(deps: ScoreSelectionDeps): void {
    renderSelectionHighlight(deps.hitTesting, deps.highlight, {
        selectedNotes: deps.selectedNotes.value,
        selectedNote: deps.selectedNote.value,
        clickedGraphicalNote: deps.clickedGraphicalNote.value,
        selectedMeasureIndex: deps.selectedMeasureIndex.value,
    });
    deps.highlight.computeSelectedNotePosition();
}

export function useScoreSelection(deps: ScoreSelectionDeps) {
    const {
        getOsmd, musicXml, hitTesting, highlight, isPlaying,
        selectedMeasureIndex, selectedMeasure, clickedGraphicalNote, selectedNote, selectedNotes,
        selectedMeasureIndices, selectedMeasures,
        currentMeasureChord, currentKeySignature, currentTimeSignature, activeEditTab, actionScope,
        structureSide, suppressMeasureAutoSelect, selectedNoteX, selectedNoteY, previewTone,
    } = deps;

    // Bereichs-Anker der Mehrfachauswahl (rein selektions-intern, nicht reaktiv/geteilt):
    // `anchorNote` = Startnote für Shift-Bereich, `anchorMeasureIndex` = Starttakt für Shift-Bereich.
    let anchorNote: NoteInfo | null = null;
    let anchorMeasureIndex: number | null = null;
    // Richtung der nächsten reinen Takt-Navigation. Wichtig für leere Takte: Previous darf dort
    // nicht über den generischen Vorwärts-Overflow wieder im Ausgangstakt landen.
    let measureNavigationDirection: 1 | -1 = 1;

    // ============ Mehrfachauswahl-Helfer ============

    function collectNoteInfo(measureIndex: number, noteIndex: number, staffIndex: number): NoteInfo | null {
        const g = hitTesting.findGraphicalNoteByMeasureIndex(measureIndex, noteIndex, staffIndex);
        return g ? getNoteInfoFromGraphicalNote(g) : null;
    }

    /** Alle Noten (inkl. Pausen) eines Takts auf einer Notenzeile als NoteInfo. */
    function collectMeasureNotes(measureIndex: number, staffIndex: number): NoteInfo[] {
        const out: NoteInfo[] = [];
        const count = hitTesting.countNotesInMeasureIndex(measureIndex, staffIndex);
        for (let i = 0; i < count; i++) {
            const info = collectNoteInfo(measureIndex, i, staffIndex);
            if (info) out.push(info);
        }
        return out;
    }

    /** Noten von `from` bis `to` (inklusiv) in linearer Reihenfolge – auf die Notenzeile des Ankers
     *  beschränkt (spiegelt die staff-erhaltende Navigation), lückensicher über Taktgrenzen. */
    function notesInRange(from: NoteInfo, to: NoteInfo): NoteInfo[] {
        const staffIndex = from.staffIndex;
        const indices = hitTesting.getMeasureIndices();
        const flat: NoteInfo[] = [];
        for (const m of indices) flat.push(...collectMeasureNotes(m, staffIndex));
        const key = (i: NoteInfo) => `${i.measureIndex}:${i.noteIndex}`;
        let a = flat.findIndex(i => key(i) === key(from));
        let b = flat.findIndex(i => key(i) === key(to));
        if (a < 0 || b < 0) return [to];
        if (a > b) [a, b] = [b, a];
        return flat.slice(a, b + 1);
    }

    /** Taktnummern von `a` bis `b` (inklusiv), gültige Reihenfolge über die existierende Taktliste. */
    function measureRange(a: number, b: number): number[] {
        const indices = hitTesting.getMeasureIndices();
        let ai = indices.indexOf(a), bi = indices.indexOf(b);
        if (ai < 0 || bi < 0) return [b];
        if (ai > bi) [ai, bi] = [bi, ai];
        return indices.slice(ai, bi + 1);
    }

    /** Alle Noten der Takte `[from … to]` auf einer Notenzeile. */
    function notesInMeasureRange(from: number, to: number, staffIndex: number): NoteInfo[] {
        const out: NoteInfo[] = [];
        for (const m of measureRange(from, to)) out.push(...collectMeasureNotes(m, staffIndex));
        return out;
    }

    function collectToggleNoteInSelection(info: NoteInfo, selectedNotesValues: NoteInfo[]): NoteInfo[] {
        const k = noteKey(info);
        const idx = selectedNotesValues.findIndex(n => noteKey(n) === k);
        if (idx >= 0) {
            const copy = [...selectedNotesValues];
            copy.splice(idx, 1);
            return copy;
        } else {
            return [...selectedNotesValues, info];
        }
    }

    /** Note in der Auswahl umschalten (Ctrl/Cmd-Klick). */
    function toggleNoteInSelection(info: NoteInfo): void {
        selectedNotes.value = collectToggleNoteInSelection(info, selectedNotes.value);
    }

    /** Noten dedupliziert (per noteKey) zu einer bestehenden Auswahl hinzufügen. */
    function collectNotes(infos: NoteInfo[], base = selectedNotes.value): NoteInfo[] {
        const seen = new Set(base.map(noteKey));
        const merged = [...base];
        for (const info of infos) {
            const k = noteKey(info);
            if (!seen.has(k)) {
                seen.add(k);
                merged.push(info);
            }
        }
        return merged;
    }

    /** Noten dedupliziert (per noteKey) zur bestehenden Auswahl hinzufügen. */
    function addNotes(infos: NoteInfo[]): void {
        selectedNotes.value = collectNotes(infos);
    }

    /** Takte, deren SÄMTLICHE Noten über **alle** Staffs/Parts aktuell in `selectedNotes` ausgewählt
     *  sind – **nur** solche Takte dürfen score-weit gelöscht werden. Ist ein Takt bei mehrteiligen
     *  Stücken nur in *einem* Part voll ausgewählt, gilt er hier NICHT als voll (→ Löschen wandelt die
     *  Noten in Pausen um). Der Soll-Bestand je (measure,staff) kommt aus **derselben** Funktion, die
     *  die Auswahl füllt (`collectMeasureNotes` – verwirft nicht-auflösbare Noten identisch), gegen die
     *  Auswahl per `noteKey` geprüft. Die reine Entscheidung liegt in `computeFullMeasures`
     *  (unit-getestet); hier wird nur die `measure → staff → noteKeys`-Map aufgebaut. */
    function fullySelectedMeasures(): number[] {
        const selectedKeys = new Set(selectedNotes.value.map(noteKey));
        const measures = new Set(selectedNotes.value.flatMap(n => n.measureIndex === undefined ? [] : [n.measureIndex]));
        const map = new Map<number, Map<number, string[]>>();
        for (const m of measures) {
            const staffMap = new Map<number, string[]>();
            for (const staff of hitTesting.staffIndicesForMeasureIndex(m)) {
                staffMap.set(staff, collectMeasureNotes(m, staff).map(noteKey));
            }
            map.set(m, staffMap);
        }
        return computeFullMeasures(selectedKeys, map);
    }

    // `selectedMeasureIndices` ist die interne Single Source of Truth für volle Takte;
    // `selectedMeasures` spiegelt nur deren gedruckte Nummern für den bestehenden UI-Vertrag.
    // `flush: 'sync'` hält es unmittelbar nach jeder Auswahl-Mutation konsistent (deleteSelection
    // liest `selectedMeasureIndices` synchron, um Takt-Löschen vs. Noten→Pausen zu wählen).
    watch(selectedNotes, () => {
        selectedMeasureIndices.value = fullySelectedMeasures();
        selectedMeasures.value = selectedMeasureIndices.value
            .map(index => hitTesting.measureNumberForIndex(index))
            .filter((number): number is number => number !== null);
    }, {flush: 'sync'});


    // ============ Navigation (Takt-/Note-Auswahl durchsteppen) ============
    // `selectedMeasureIndex` ist die Source of Truth: ein watch(selectedMeasureIndex) positioniert
    // Cursor + wählt eine Note (Takt↔Note gekoppelt), aber NUR bei reiner Takt-Navigation – ein Klick,
    // der die primäre Note bereits im Zieltakt gesetzt hat, wird übersprungen (sonst überschriebe die
    // Einzelnoten-Auto-Auswahl eine Mehrfachauswahl). `selectNote` ist rekursiv selbst-überlaufend
    // (existiert die Note nicht, springt es in den Nachbartakt); am Stück-Rand wird geclampt (kein Wrap).

    /** Wählt EINE Note aus (Einzelauswahl: setzt `selectedNotes` auf genau diese Note) und aktualisiert
     *  Auswahl-State + Highlight + Panel-Position. Rekursiv selbst-überlaufend über Taktgrenzen; am
     *  Stück-Rand geclampt. Setzt `selectedNote` VOR `selectedMeasure`, damit der Takt-Watch die
     *  Auswahl als konsistent erkennt (kein erneuter Auto-Select). */
    function selectNote(
        measureIndex: number,
        noteIndex: number,
        staffIndex = 0,
        direction: 1 | -1 = noteIndex < 0 ? -1 : 1,
    ): void {
        const indices = hitTesting.getMeasureIndices();
        if (!indices.length) return;
        let idx = indices.indexOf(measureIndex);
        if (idx < 0) {
            idx = measureIndex < indices[0]! ? 0 : indices.length - 1;
            measureIndex = indices[idx]!;
        }

        if (noteIndex < 0) {
            if (idx === 0) {
                noteIndex = 0;                                   // Stück-Anfang → erste Note, clampen
            } else {
                const prev = indices[idx - 1]!;                  // letzte Note des Vortakts (gleiche Zeile)
                selectNote(prev, hitTesting.countNotesInMeasureIndex(prev, staffIndex) - 1, staffIndex, -1);
                return;
            }
        }

        const gNote = hitTesting.findGraphicalNoteByMeasureIndex(measureIndex, noteIndex, staffIndex);
        if (!gNote) {
            if (direction < 0) {
                if (idx === 0) return;                           // Stück-Anfang → bestehende Auswahl bleibt
                const prev = indices[idx - 1]!;
                selectNote(prev, hitTesting.countNotesInMeasureIndex(prev, staffIndex) - 1, staffIndex, -1);
                return;
            }
            if (idx >= indices.length - 1) return;               // Stück-Ende → clampen (kein Wrap)
            selectNote(indices[idx + 1]!, 0, staffIndex, 1);     // über Taktende → erste Note Folgetakt
            return;
        }

        const noteInfo = getNoteInfoFromGraphicalNote(gNote);
        clickedGraphicalNote.value = gNote;
        if (noteInfo) {
            selectedNote.value = noteInfo;
            selectedNotes.value = [noteInfo];                    // Einzelauswahl (Navigation ⇒ eine Note)
            anchorNote = noteInfo;
        }
        anchorMeasureIndex = null;
        selectedMeasureIndex.value = measureIndex;
        selectedMeasure.value = noteInfo?.measureNumber ?? hitTesting.measureNumberForIndex(measureIndex);
        const gMeasure = hitTesting.findGraphicalMeasureByIndex(measureIndex, staffIndex);
        if (gMeasure) highlight.highlightMeasure(gMeasure);
        highlight.highlightNote(gNote);
        highlight.computeSelectedNotePosition();
        soundSelectedNote(noteInfo);
    }

    /** Reiner Takt-Kontext ohne Notenauswahl: Navigationsrichtung zurücksetzen, internen Index und
     *  gedruckte Nummer setzen. Der `watch(selectedMeasureIndex)` unten wählt daraufhin selbst eine
     *  Note des Takts, falls die primäre noch woanders liegt. */
    function setMeasureContext(measureIndex: number): void {
        measureNavigationDirection = 1;
        selectedMeasureIndex.value = measureIndex;
        selectedMeasure.value = hitTesting.measureNumberForIndex(measureIndex);
    }

    // Source of Truth für reine Takt-Navigation: eine Änderung von `selectedMeasureIndex` (selectNext*/Prev*
    // Measure, ChordEditView-Pfeiltasten) positioniert Cursor + markiert eine Note. Bei einem Klick, der
    // `selectedNote` bereits im Zieltakt gesetzt hat, ist nichts zu tun (schützt die Mehrfachauswahl).
    watch(selectedMeasureIndex, (measureIndex) => {
        // Einmal-Unterdrückung (insertMeasure): der Kontext wandert programmatisch VOR dem
        // Re-Render – die Auto-Wahl liefe gegen das alte Notenbild (falsche Note + Vorschau-Ton,
        // am Stückende klemmte der Stale-Clamp den Kontext auf den Referenztakt zurück).
        if (suppressMeasureAutoSelect.value) {
            suppressMeasureAutoSelect.value = false;
            measureNavigationDirection = 1;
            return;
        }
        if (measureIndex === null) {
            measureNavigationDirection = 1;
            return;
        }
        if (selectedNote.value?.measureIndex === measureIndex) {
            measureNavigationDirection = 1;
            return; // Klick hat Note schon konsistent gesetzt
        }
        const direction = measureNavigationDirection;
        measureNavigationDirection = 1;
        const staffIndex = selectedNote.value?.staffIndex ?? 0;
        const maxIndex = Math.max(0, hitTesting.countNotesInMeasureIndex(measureIndex, staffIndex) - 1);
        selectNote(measureIndex, Math.min(selectedNote.value?.noteIndex ?? 0, maxIndex), staffIndex, direction);
    });

    function selectNextMeasure(): void {
        const indices = hitTesting.getMeasureIndices();
        if (!indices.length) return;
        const idx = selectedMeasureIndex.value === null ? -1 : indices.indexOf(selectedMeasureIndex.value);
        measureNavigationDirection = 1;
        selectedMeasureIndex.value = indices[Math.min(idx + 1, indices.length - 1)]!;
    }

    /** Nächste Note. Mit `skipSamePosition` wird der **Rest der aktuellen Zählzeit**
     *  (Akkordtöne, gleichzeitige Stimmen – Abgrenzung in `useScoreHitTesting.onsetGroupBounds`)
     *  übersprungen und die ERSTE Note der nächsten Zählzeit gewählt; das braucht die
     *  Gesangstext-Sitzung, die **eine Silbe je Zählzeit** vergibt.
     *
     *  **Default aus (nicht umdrehen):** Pfeiltasten und Toolbar müssen jeden Akkordton einzeln
     *  erreichen, sonst sind die Noten-Edits (Ton entfernen, transponieren) auf Akkordtönen nicht
     *  mehr bedienbar. Am Taktende braucht es keinen Sonderfall: `selectNote` läuft bei einem zu
     *  großen Index selbst in den Folgetakt auf Index 0 über – dort immer die erste Note der
     *  ersten Zählzeit. */
    function selectNextNote(skipSamePosition = false): void {
        const sel = selectedNote.value;
        if (sel?.measureIndex === undefined) {
            if (selectedMeasureIndex.value !== null) selectNote(selectedMeasureIndex.value, 0);
            return;
        }
        const end = skipSamePosition
            ? hitTesting.onsetGroupBounds(sel.measureIndex, sel.noteIndex, sel.staffIndex)?.end
            : undefined;
        selectNote(sel.measureIndex, end ?? sel.noteIndex + 1, sel.staffIndex);
    }

    /** Vorige Note; `skipSamePosition` wie in {@link selectNextNote}, nur rückwärts – Ziel ist die
     *  **erste** Note der vorangehenden Zählzeit.
     *
     *  Anders als vorwärts reicht der Clamp von `selectNote` hier nicht: sein `noteIndex < 0`-Zweig
     *  landet auf der **letzten** Note des Vortakts, also ggf. mitten in einem Akkord. Deshalb wird
     *  der Gruppenanfang explizit aufgelöst; `selectNote` selbst bleibt unangetastet (seine Rekursion
     *  und die Rand-Clamps sind die empfindlichste Stelle der Navigation). Das Einrasten ist
     *  **kosmetisch** – es hält Markierung und Anker des schwebenden Eingabefelds symmetrisch zum
     *  Vorwärtsschritt; der Schreibpfad leitet Akkordtöne ohnehin auf die Primärnote der Gruppe um
     *  (`musicXmlDom/lyrics.ts`, `chordGroupOf(...)[0]`). */
    function selectPreviousNote(skipSamePosition = false): void {
        const sel = selectedNote.value;
        if (sel?.measureIndex === undefined) {
            if (selectedMeasureIndex.value !== null) selectNote(selectedMeasureIndex.value, 0);
            return;
        }
        if (!skipSamePosition) {
            selectNote(sel.measureIndex, sel.noteIndex - 1, sel.staffIndex);
            return;
        }
        const start = hitTesting.onsetGroupBounds(sel.measureIndex, sel.noteIndex, sel.staffIndex)?.start
            ?? sel.noteIndex;
        if (start > 0) {
            const previous = hitTesting.onsetGroupBounds(sel.measureIndex, start - 1, sel.staffIndex);
            selectNote(sel.measureIndex, previous?.start ?? start - 1, sel.staffIndex);
            return;
        }
        // Taktanfang → letzte Zählzeit des Vortakts; am Stückanfang bleibt es bei Index 0.
        const indices = hitTesting.getMeasureIndices();
        const idx = indices.indexOf(sel.measureIndex);
        const previousMeasure = idx > 0 ? indices[idx - 1] : undefined;
        if (previousMeasure === undefined) {
            selectNote(sel.measureIndex, 0, sel.staffIndex);
            return;
        }
        const last = hitTesting.countNotesInMeasureIndex(previousMeasure, sel.staffIndex) - 1;
        const group = hitTesting.onsetGroupBounds(previousMeasure, last, sel.staffIndex);
        selectNote(previousMeasure, group?.start ?? last, sel.staffIndex, -1);
    }

    // ============ Auswahl kumulativ erweitern (Strg+Pfeil / Strg+Umschalt+Pfeil) ============

    /** Nachbar-Note (`dir` +1 = nächste, -1 = vorige) auf derselben Notenzeile über das flache
     *  Noten-Array (wie `notesInRange`); `null` am Stück-Rand (kein Wrap). */
    function adjacentNote(from: NoteInfo, dir: 1 | -1): NoteInfo | null {
        const indices = hitTesting.getMeasureIndices();
        const flat: NoteInfo[] = [];
        for (const m of indices) flat.push(...collectMeasureNotes(m, from.staffIndex));
        const key = (i: NoteInfo) => `${i.measureIndex}:${i.noteIndex}`;
        const cur = flat.findIndex(i => key(i) === key(from));
        if (cur < 0) return null;
        const next = cur + dir;
        return next >= 0 && next < flat.length ? flat[next]! : null;
    }

    /** Erweitert die Auswahl **kumulativ** um die vorige/nächste Note (`dir` -1/+1); die primäre Note
     *  wandert mit, sodass wiederholtes Aufrufen die Auswahl fortlaufend vergrößert (dedup via `addNotes`). */
    function extendSelectionByNote(dir: 1 | -1): void {
        const sel = selectedNote.value;
        if (!sel) return;
        const next = adjacentNote(sel, dir);
        if (!next) return;
        addNotes([next]);
        const g = next.measureIndex === undefined
            ? null
            : hitTesting.findGraphicalNoteByMeasureIndex(next.measureIndex, next.noteIndex, next.staffIndex);
        setPrimaryNote(next, g);
        refreshSelectionHighlight(deps);
    }

    /** Erweitert die Auswahl **kumulativ** um den vorigen/nächsten Takt (`dir` -1/+1): alle Noten des
     *  Nachbartakts werden hinzugefügt (`selectedMeasures` folgt via watch); die primäre Note wandert
     *  auf die erste/letzte Note des neuen Takts. */
    function extendSelectionByMeasure(dir: 1 | -1): void {
        const staffIndex = selectedNote.value?.staffIndex ?? 0;
        const current = selectedNote.value?.measureIndex ?? selectedMeasureIndex.value;
        if (current == null) return;
        const indices = hitTesting.getMeasureIndices();
        const idx = indices.indexOf(current);
        if (idx < 0) return;
        const nextIdx = idx + dir;
        if (nextIdx < 0 || nextIdx >= indices.length) return;
        const notes = collectMeasureNotes(indices[nextIdx]!, staffIndex);
        if (!notes.length) return;
        addNotes(notes);
        const primary = dir > 0 ? notes[0]! : notes[notes.length - 1]!;
        const g = primary.measureIndex === undefined
            ? null
            : hitTesting.findGraphicalNoteByMeasureIndex(primary.measureIndex, primary.noteIndex, primary.staffIndex);
        setPrimaryNote(primary, g);
        refreshSelectionHighlight(deps);
    }

    /** Erweitert die Auswahl **kumulativ** auf den Part/die Notenzeile **darüber/darunter** (`dir`
     *  -1 = kleinerer staffIndex/oben, +1 = größerer/unten): fügt für **alle** aktuell ausgewählten
     *  Takte die Noten des Nachbar-Staffs hinzu (`selectedMeasures` folgt via watch). Nur bei
     *  mehrteiligen Stücken wirksam; am Rand (kein Part drüber/drunter) No-op. Die primäre Note wandert
     *  in den neuen Part (primärer Takt). */
    function extendSelectionByPart(dir: 1 | -1): void {
        const currentStaff = selectedNote.value?.staffIndex ?? 0;
        const primaryMeasure = selectedNote.value?.measureIndex ?? selectedMeasureIndex.value;
        if (primaryMeasure == null) return;
        const staffs = hitTesting.staffIndicesForMeasureIndex(primaryMeasure);
        const idx = staffs.indexOf(currentStaff);
        if (idx < 0) return;
        const nextIdx = idx + dir;
        if (nextIdx < 0 || nextIdx >= staffs.length) return;      // kein Part drüber/drunter → clamp
        const nextStaff = staffs[nextIdx]!;
        const measures = [...new Set(selectedNotes.value.flatMap(n => n.measureIndex === undefined ? [] : [n.measureIndex]))];
        const toAdd = measures.flatMap(m => collectMeasureNotes(m, nextStaff));
        if (!toAdd.length) return;
        addNotes(toAdd);
        const primaryNotes = collectMeasureNotes(primaryMeasure, nextStaff);
        const primary = primaryNotes.length ? (dir > 0 ? primaryNotes[0]! : primaryNotes[primaryNotes.length - 1]!) : null;
        if (primary) {
            const g = primary.measureIndex === undefined
                ? null
                : hitTesting.findGraphicalNoteByMeasureIndex(primary.measureIndex, primary.noteIndex, primary.staffIndex);
            setPrimaryNote(primary, g);
        }
        refreshSelectionHighlight(deps);
    }

    // ============ Auswahl / Klick / Panels ============

    function clearSelection(): void {
        cancelPendingHover();
        clearHover();
        selectedMeasureIndex.value = null;
        selectedMeasure.value = null;
        clickedGraphicalNote.value = null;
        selectedNote.value = null;
        selectedNotes.value = [];
        selectedMeasureIndices.value = [];
        selectedMeasures.value = [];
        anchorNote = null;
        anchorMeasureIndex = null;
        currentMeasureChord.value = null;
        currentKeySignature.value = null;
        selectedNoteX.value = null;
        selectedNoteY.value = null;
        highlight.clearSelectionColor();
        highlight.clearHighlight();
        if (!isPlaying.value) getOsmd()?.cursor?.hide();
    }

    /** Setzt die primäre Note (zuletzt geklickt): treibt Cursor + Panel + Panels. `selectedMeasure`
     *  wird NACH `selectedNote` gesetzt, damit der Takt-Watch die Auswahl als konsistent erkennt. */
    /**
     * Lässt die neu zur primären gewordene Note hören – **genau diese eine**, nicht ihre
     * Akkord-Nachbarn: beim Auswählen will man wissen, welchen Ton man angeklickt hat.
     *
     * Die Tonhöhe kommt direkt aus der `NoteInfo` (dieselbe OSMD-Quelle, die auch die roten
     * unspielbaren Noten bestimmt) statt aus dem XML – das spart bei jedem Klick und jedem
     * Navigationsschritt einen vollständigen `DOMParser`-Durchlauf über die ganze Partitur.
     *
     * Bewusst an den beiden nutzer-initiierten Funnels `setPrimaryNote`/`selectNote` statt an einem
     * `watch(selectedNote)`: `refreshStaleSelection()` weist `selectedNote` nach **jedem** Re-Render
     * neu zu (es löst veraltete Adressen frisch aus OSMD auf) – ein Watcher klänge damit nach jeder
     * Bearbeitung ein zweites Mal.
     */
    function soundSelectedNote(info: NoteInfo | null): void {
        if (!info || info.isRest || !info.step) return;
        previewTone([pitchToMidi(info.step, info.alter, info.octave)]);
    }

    /** Die vier Zuweisungen der Primärnoten-Setzung – `selectedNote` VOR `selectedMeasureIndex`
     *  (der Takt-Watch erkennt die Auswahl nur so als konsistent). Stumm; der Ton hängt am
     *  Funnel {@link setPrimaryNote} – die Rubber-Band-Auswahl ({@link applyDragSelection})
     *  nutzt bewusst nur diesen Teil. */
    function assignPrimaryNote(info: NoteInfo, gNote: GraphicalNote | null): void {
        selectedNote.value = info;
        clickedGraphicalNote.value = gNote;
        measureNavigationDirection = 1;
        selectedMeasureIndex.value = info.measureIndex ?? null;
        selectedMeasure.value = info.measureNumber;
    }

    function setPrimaryNote(info: NoteInfo, gNote: GraphicalNote | null): void {
        soundSelectedNote(info);
        assignPrimaryNote(info, gNote);
    }

    /**
     * Commit einer Rubber-Band-Auswahl (Konsument: `useScoreDragSelection`, aufgerufen genau
     * einmal beim Loslassen) – der EINZIGE Mutations-Punkt der Geste; die Drag-Maschine selbst
     * hält keinen Selektions-State.
     *
     * - `selectedNotes` wird genau **einmal** zugewiesen (der `flush:'sync'`-Watch oben leitet
     *   `selectedMeasures` pro Zuweisung ab – pro Drag-Frame darf hier nichts schreiben).
     * - **Stumm** (bewusst `assignPrimaryNote` statt `setPrimaryNote`): eine Rechteck-Auswahl
     *   „meint" keine einzelne Tonhöhe – ein Ton nach dem Aufziehen über 30 Noten wäre
     *   irreführend. Klick/Navigation klingen unverändert.
     * - Leeres Rechteck ohne Modifier = bewusstes „ins Leere ziehen" → Auswahl aufheben;
     *   additiv (Strg/Cmd) bleibt die bestehende Auswahl unangetastet.
     * - Primärnote = letzter Treffer in Layout-Reihenfolge; sie wird zugleich Shift-Anker
     *   (ein folgender Shift-Klick erweitert ab dem Rechteck-Ende).
     */
    function applyDragSelection(hits: DragSelectionHit[], opts: {additive: boolean}): void {
        if (!hits.length) {
            if (!opts.additive) clearSelection();
            return;
        }
        const infos = collectNotes(hits.map(h => h.info), []);
        selectedNotes.value = opts.additive ? collectNotes(infos) : infos;
        const primary = hits[hits.length - 1]!;
        anchorNote = primary.info;
        anchorMeasureIndex = null;
        assignPrimaryNote(primary.info, primary.gNote);
        activeEditTab.value = 'note';
        actionScope.value = 'note';
        refreshSelectionHighlight(deps);
    }

    let pendingHoverEvent: MouseEvent | null = null;
    let hoverAnimationFrame: number | null = null;
    let lastHoverKey: string | null = null;

    // Note unter dem Zeiger + Zeigerposition – die Datenbasis des Tonhöhen-Tooltips. Bewusst NICHT
    // in `ScoreSelectionDeps`: kein anderes Sub-Composable liest sie, nur der Host rendert daraus.
    // `hoverX`/`hoverY` als zwei primitive Refs (wie `selectedNoteX`/`selectedNoteY`), damit pro
    // Frame keine Objekt-Allokation anfällt.
    const hoveredNote = shallowRef<NoteInfo | null>(null);
    const hoverX = ref<number | null>(null);
    const hoverY = ref<number | null>(null);
    let lastHoveredNoteKey: string | null = null;

    function committedSelectionKey(): string {
        return selectedNotes.value.map(noteKey).join('|');
    }

    function cancelPendingHover(): void {
        if (hoverAnimationFrame !== null) cancelAnimationFrame(hoverAnimationFrame);
        hoverAnimationFrame = null;
        pendingHoverEvent = null;
        lastHoverKey = null;
    }

    /** Verwirft den Tooltip-Zustand. Bewusst GETRENNT von {@link cancelPendingHover}: ein Linksklick
     *  setzt nur die Render-Dedup zurück – der Zeiger steht danach weiter auf derselben Note, der
     *  Tooltip soll also nicht wegblinken. */
    function clearHover(): void {
        hoveredNote.value = null;
        hoverX.value = null;
        hoverY.value = null;
        lastHoveredNoteKey = null;
    }

    /** Veröffentlicht die Note unter dem Zeiger für den Tooltip.
     *
     *  **Die Identität wechselt nur bei einem echten Notenwechsel (kritisch – nicht zu einer
     *  unbedingten Zuweisung rückbauen):** `getNoteInfoFromGraphicalNote` liefert bei jedem Aufruf
     *  ein FRISCHES Objekt, und der Griff-Abruf der Ausgabe beobachtet dessen Objektidentität über
     *  einen 200-ms-`watchDebounced`. Eine Zuweisung pro Frame startet den Debounce bei jeder
     *  Mausbewegung neu – der klingende Ton käme nie an, und jedes Mikro-Ruckeln beim Verweilen
     *  feuerte einen Request mit dem vollständigen MusicXML im Body.
     *
     *  Die **Position** wird dagegen bei jedem Frame geschrieben, damit die Box dem Zeiger folgt. */
    function publishHover(info: NoteInfo, event: MouseEvent): void {
        const key = noteKey(info);
        if (key !== lastHoveredNoteKey) {
            lastHoveredNoteKey = key;
            hoveredNote.value = info;
        }
        hoverX.value = event.clientX;
        hoverY.value = event.clientY;
    }

    /** Berechnet und rendert ausschließlich eine temporäre Hover-Vorschau (Hover-Layer + cursors[2]).
     *  Auswahl-Refs, Auswahl-Färbung, grüner Note-Cursor, Bereichs-Anker und Edit-State bleiben
     *  dabei unverändert.
     *
     *  **Der freie Taktbereich zeigt nur MIT Auswahl-Modifier eine Vorschau (Ctrl/Cmd oder Shift) –
     *  nicht auf eine modifierlose Takt-Vorschau rückbauen:** zwischen zwei Noten liegt immer freier
     *  Raum (`noteAtWithinThreshold` trifft nur in einem schmalen Korridor um den Notenkopf), eine
     *  bedingungslose Takt-Vorschau ließe die Partitur also bei jeder Mausbewegung aufblitzen. Die
     *  beiden Modifier sind genau die, unter denen ein Klick den Takt auch wirklich selektiert
     *  (Spiegel des Gates in {@link handleContainerClick}) – die Vorschau bleibt damit wahrheitsgemäß.
     *  Ohne Modifier fällt der Takt-Treffer auf denselben Pfad wie „nichts getroffen": Tooltip
     *  räumen und – nur falls im Frame davor eine Vorschau stand – die Auswahl-Färbung restaurieren. */
    function renderContainerHover(event: MouseEvent): void {
        const additive = event.ctrlKey || event.metaKey;
        const range = event.shiftKey;
        // hitAt: EINE Koordinaten-Transformation + EIN Takt-Scan pro Hover-Frame.
        const {measure, note: nearestNote} = hitTesting.hitAt(event);

        if (nearestNote) {
            const info = getNoteInfoFromGraphicalNote(nearestNote);
            if (!info) {
                clearHover();
                return;
            }

            // VOR der `hoverKey`-Dedup: die dedupliziert ausschliesslich das teure Neu-Einfaerben.
            // Stuende der Aufruf dahinter, bliebe der Tooltip beim Bewegen innerhalb derselben Note
            // an seinem Eintrittspunkt kleben.
            publishHover(info, event);

            const hoverKey = [
                'note', noteKey(info), additive, range,
                anchorNote ? noteKey(anchorNote) : '',
                committedSelectionKey(),
            ].join(':');
            if (hoverKey === lastHoverKey) return;
            lastHoverKey = hoverKey;
            const previewNotes = range && anchorNote
                ? notesInRange(anchorNote, info)
                : additive
                    ? collectToggleNoteInSelection(info, selectedNotes.value)
                    : [info];

            renderHoverPreview(hitTesting, highlight, {
                selectedNotes: previewNotes,
                selectedNote: info,
                clickedGraphicalNote: nearestNote,
                selectedMeasureIndex: info.measureIndex ?? null,
            });
            return;
        }

        if (measure !== null && (additive || range)) {
            clearHover();
            const measureIndex = hitTesting.measureIndexOf(measure);
            const staffIndex = measure.ParentStaff?.idInMusicSheet ?? 0;
            const rangeAnchor = anchorMeasureIndex;
            const hoverKey = [
                'measure', measureIndex, staffIndex, additive, range,
                rangeAnchor ?? '',
                committedSelectionKey(),
            ].join(':');
            if (hoverKey === lastHoverKey) return;
            lastHoverKey = hoverKey;
            const measureNotes = collectMeasureNotes(measureIndex, staffIndex);
            const previewNotes = range && rangeAnchor !== null
                ? notesInMeasureRange(rangeAnchor, measureIndex, staffIndex)
                : additive
                    ? collectNotes(measureNotes, selectedNotes.value)
                    : measureNotes;
            const primary = measureNotes[0] ?? null;

            renderHoverPreview(hitTesting, highlight, {
                selectedNotes: previewNotes,
                selectedNote: primary,
                clickedGraphicalNote: primary
                    ? hitTesting.findGraphicalNoteByMeasureIndex(
                        measureIndex,
                        primary.noteIndex,
                        primary.staffIndex,
                    )
                    : null,
                selectedMeasureIndex: primary?.measureIndex ?? measureIndex,
            });
            return;
        }

        clearHover();
        if (lastHoverKey !== null) {
            lastHoverKey = null;
            restoreSelectionHighlight();
        }
    }

    /** Mousemove wird auf einen Render pro Browser-Frame begrenzt; im Frame zählt nur das
     *  jeweils neueste Event. */
    function handleContainerHover(event: MouseEvent): void {
        pendingHoverEvent = event;
        if (hoverAnimationFrame !== null) return;
        hoverAnimationFrame = requestAnimationFrame(() => {
            hoverAnimationFrame = null;
            const latestEvent = pendingHoverEvent;
            pendingHoverEvent = null;
            if (latestEvent) renderContainerHover(latestEvent);
        });
    }

    function handleContainerMouseLeave(): void {
        cancelPendingHover();
        clearHover();
        restoreSelectionHighlight();
    }

    function handleContainerClick(event: MouseEvent): void {
        cancelPendingHover();
        const additive = event.ctrlKey || event.metaKey;
        const range = event.shiftKey;
        // hitAt: EINE Koordinaten-Transformation + EIN Takt-Scan pro Klick.
        const {pos, measure, note: nearestNote} = hitTesting.hitAt(event);
        if (nearestNote) {
            // ---- Klick auf/nahe einer Note ----
            const info = getNoteInfoFromGraphicalNote(nearestNote);
            if (!info) return;

            if (range && anchorNote) {
                selectedNotes.value = notesInRange(anchorNote, info);
            } else if (additive) {
                toggleNoteInSelection(info);
                anchorNote = info;
            } else {
                selectedNotes.value = [info];
                anchorNote = info;
            }
            anchorMeasureIndex = null;
            setPrimaryNote(info, nearestNote);
            activeEditTab.value = 'note';
            actionScope.value = 'note';
        } else if (measure !== null) {
            // ---- Klick in den freien Taktbereich (nur mit Auswahl-Modifier) → ganzer Takt ----
            const m = hitTesting.measureIndexOf(measure);
            const staffIndex = measure.ParentStaff?.idInMusicSheet ?? 0;
            const selectsMeasure = additive || range;

            // **Ohne Auswahl-Modifier wählt der freie Taktbereich NICHTS aus (nicht rückbauen)** –
            // Spiegel des Gates in `renderContainerHover`, dort steht die Begründung. Einzige
            // Ausnahme ist der **Doppelklick**: er setzt nur den Takt-Kontext, damit der Host
            // (`MusicXmlPreview.onContainerClick`) das Aktionsmenü für diesen Takt öffnen kann; die
            // eigentliche Auswahl übernimmt der `watch(selectedMeasureIndex)` (er markiert die erste
            // Note des Takts). Bewusst KEIN `return` aus diesem Zweig: der gemeinsame Schwanz unten
            // ruft `refreshSelectionHighlight` – das räumt den Hover-Layer ab
            // (`cancelPendingHover` setzt nur `lastHoverKey` zurück, es entfärbt nichts).
            if (!selectsMeasure && event.detail !== 2) return;

            // ERST hier – ein folgenlos ignorierter einfacher Klick darf den Geltungsbereich des
            // Aktionsmenüs nicht umschalten.
            actionScope.value = 'measure';

            if (!selectsMeasure) {
                setMeasureContext(m);
                // Doppelklick nahe der Taktkante: direkt den Struktur-Editor mit der getroffenen
                // Seite öffnen (der `watch(activeEditTab)` im Host öffnet das Overlay); mittig
                // bleibt es beim Aktionsmenü.
                const side = hitTesting.barlineSideAt(pos, measure);
                if (side) {
                    structureSide.value = side;
                    activeEditTab.value = 'structure';
                }
            } else {
                if (range && anchorMeasureIndex != null) {
                    selectedNotes.value = notesInMeasureRange(anchorMeasureIndex, m, staffIndex);
                } else if (additive) {
                    addNotes(collectMeasureNotes(m, staffIndex));
                    anchorMeasureIndex = m;
                } else {
                    // Shift ohne Takt-Anker: hier beginnt der Bereich.
                    selectedNotes.value = collectMeasureNotes(m, staffIndex);
                    anchorMeasureIndex = m;
                }

                // Primär = erste Note des (zuletzt geklickten) Takts, sonst reiner Takt-Kontext.
                const primary = collectMeasureNotes(m, staffIndex)[0] ?? null;
                if (primary) {
                    anchorNote = primary;
                    setPrimaryNote(primary, hitTesting.findGraphicalNoteByMeasureIndex(m, primary.noteIndex, primary.staffIndex));
                } else {
                    clickedGraphicalNote.value = null;
                    selectedNote.value = null;
                    setMeasureContext(m);
                }
            }
        } else {
            // Klick außerhalb aller Takte → Auswahl unverändert lassen.
            return;
        }

        refreshSelectionHighlight(deps);
    }

    /** Rechtsklick-Trefferprüfung. Sie entscheidet den **Geltungsbereich** des Aktionsmenüs:
     *
     * - **Note getroffen** → wie ein Links-Klick auf eine einzelne Note (keine
     *   Mehrfachauswahl-Modifier): sie wird zur alleinigen, primären Auswahl.
     * - **Freie Taktfläche** → reiner Takt-Kontext; der Aufrufer öffnet damit das Takt-Menü
     *   (Akkord/Tonart/Taktart/…). Die bestehende Notenauswahl bleibt **absichtlich stehen**: der
     *   `watch(selectedMeasureIndex)` zieht die Markierung ohnehin in den geklickten Takt, während
     *   ein `clearSelection()` zusätzlich `selectedNoteX/Y` nullte (schlechter Editor-Anker) und
     *   eine für die Toolbar aufgebaute Mehrfachauswahl zerstörte.
     * - **`null`** → außerhalb **jedes** Takts; nur dann lässt der Aufrufer das native
     *   Browser-Kontextmenü zu.
     *
     * Bewusst **ohne** `barlineSideAt`: die Abkürzung in den Struktur-Editor ist eine
     * Doppelklick-Geste, ein Rechtsklick landet immer erst im Menü. */
    function handleContainerContextMenu(event: MouseEvent): ScoreContextMenuHit | null {
        cancelPendingHover();
        // Vor dem Menue raeumen: dessen ganzflaechiger Backdrop verschluckt `mousemove`, der
        // Tooltip hinge sonst als Geisterkasten daneben fest. Muss vor JEDEM Return laufen.
        clearHover();
        // EINE Koordinaten-Transformation + EIN Takt-Scan liefern Note UND Takt (wie der Links-Klick).
        const {measure, note: nearestNote} = hitTesting.hitAt(event);
        const info = nearestNote ? getNoteInfoFromGraphicalNote(nearestNote) : null;

        if (info && nearestNote) {
            selectedNotes.value = [info];
            anchorNote = info;
            anchorMeasureIndex = null;
            setPrimaryNote(info, nearestNote);
            activeEditTab.value = 'note';
            actionScope.value = 'note';
            refreshSelectionHighlight(deps);
            return {scope: 'note', note: info};
        }

        if (!measure) return null;

        const measureIndex = hitTesting.measureIndexOf(measure);
        actionScope.value = 'measure';
        setMeasureContext(measureIndex);
        // Ein veralteter Editor-Reiter (Toolbar-Weg) zeigte sonst den Editor statt des Takt-Menüs.
        activeEditTab.value = 'note';
        refreshSelectionHighlight(deps);
        return {scope: 'measure', measureIndex};
    }

    function openChordEditor(): void {
        if (selectedMeasure.value === null || !musicXml.value) return;
        currentMeasureChord.value = getChordFromMeasure(
            musicXml.value, selectedMeasure.value, undefined, selectedMeasureIndex.value ?? undefined,
        );
        activeEditTab.value = 'chord';
    }

    function openKeySignatureEditor(): void {
        if (selectedMeasure.value === null || !musicXml.value) return;
        currentKeySignature.value = getKeySignatureFromMeasure(
            musicXml.value, selectedMeasure.value, undefined, selectedMeasureIndex.value ?? undefined,
        );
        activeEditTab.value = 'key';
    }

    function openTimeSignatureEditor(): void {
        if (selectedMeasure.value === null || !musicXml.value) return;
        currentTimeSignature.value = getTimeSignatureFromMeasure(
            musicXml.value, selectedMeasure.value, undefined, selectedMeasureIndex.value ?? undefined,
        );
        activeEditTab.value = 'time';
    }

    function openBassStyleEditor(): void {
        // Kein Vorab-Lesen noetig: die Spielweise kommt reaktiv aus `bassStyles` (Score-Metadaten).
        if (selectedMeasure.value === null) return;
        activeEditTab.value = 'bass';
    }

    function openStructureEditor(): void {
        // Kein Vorab-Lesen nötig: der Zustand kommt reaktiv aus `currentMeasureStructure` (XML).
        if (selectedMeasureIndex.value === null) return;
        structureSide.value = null; // Menü/Toolbar-Pfad: keine vorgewählte Seite.
        activeEditTab.value = 'structure';
    }

    function openMeasuresEditor(): void {
        // Takt einfügen/löschen – bewusst ein EIGENER Reiter (nicht Teil des Struktur-Editors).
        if (selectedMeasureIndex.value === null) return;
        activeEditTab.value = 'measures';
    }

    /** Ersetzt Infos mit veraltetem flachen `noteIndex` (`-1`, gesetzt von
     *  `remapSelectionAfterIndexEdits` nach dem Einfügen/Entfernen von Akkordtönen) durch frisch aus
     *  OSMD abgeleitete Infos – Lookup über die stabile Stimmen-Position
     *  (`hitTesting.findGraphicalNoteByVoiceIndex`). Nicht mehr auffindbare Infos fallen weg; die
     *  primäre Note folgt (per `noteKey`), sonst wird die erste verbleibende primär. */
    function refreshStaleSelection(): void {
        if (!selectedNotes.value.some(i => i.noteIndex < 0)) return;
        const primaryKey = selectedNote.value ? noteKey(selectedNote.value) : null;
        const fresh: NoteInfo[] = [];
        let primary: NoteInfo | null = null;
        let primaryGraphical: GraphicalNote | null = null;
        for (const info of selectedNotes.value) {
            let resolved: NoteInfo | null = info;
            let g: GraphicalNote | null = null;
            if (info.noteIndex < 0) {
                g = info.measureIndex === undefined ? null : hitTesting.findGraphicalNoteByVoiceIndex(
                    info.measureIndex, info.staffIndex, info.voice, info.voiceNoteIndex,
                );
                resolved = g ? getNoteInfoFromGraphicalNote(g) : null;
            }
            if (!resolved) continue;
            fresh.push(resolved);
            if (primaryKey !== null && noteKey(info) === primaryKey) {
                primary = resolved;
                primaryGraphical = g;
            }
        }
        selectedNotes.value = fresh;
        if (primary) {
            selectedNote.value = primary;
            if (primaryGraphical) clickedGraphicalNote.value = primaryGraphical;
        } else if (selectedNote.value && !fresh.some(i => noteKey(i) === primaryKey)) {
            selectedNote.value = fresh[0] ?? null;
        }
    }

    /** Nach JEDEM Render (Edit, Zoom, Resize) die Auswahl-Markierung wiederherstellen, damit die
     *  bearbeitete(n) Note(n)/der Takt markiert bleiben. Findet Note/Takt über die musikalische
     *  Identität (nicht Pixel) und färbt die GESAMTE Auswahl neu rot. Bei externem Inhaltswechsel
     *  (neue Datei/Transpose) leert clearSelection die Auswahl vorher. */
    function restoreSelectionHighlight(): void {
        refreshStaleSelection();
        if (selectedNotes.value.length && selectedMeasureIndex.value !== null) {
            if (selectedNote.value?.measureIndex !== undefined) {
                clickedGraphicalNote.value = hitTesting.findGraphicalNoteByMeasureIndex(
                    selectedNote.value.measureIndex, selectedNote.value.noteIndex, selectedNote.value.staffIndex,
                );
            }
            highlight.highlightNotes(
                graphicalNotesForSelection(hitTesting, {
                    selectedNotes: selectedNotes.value,
                    selectedNote: selectedNote.value,
                    clickedGraphicalNote: clickedGraphicalNote.value,
                    selectedMeasureIndex: selectedMeasureIndex.value,
                }),
                clickedGraphicalNote.value
            );
        } else {
            highlight.clearSelectionColor();
            if (!isPlaying.value) getOsmd()?.cursor?.hide();
        }
        if (selectedMeasureIndex.value !== null) {
            const measure = hitTesting.findGraphicalMeasureByIndex(
                selectedMeasureIndex.value, selectedNote.value?.staffIndex,
            );
            if (measure) highlight.highlightMeasure(measure, true);
        } else {
            highlight.clearHighlight();
        }
        highlight.computeSelectedNotePosition();
    }

    return {
        selectNote,
        selectNextMeasure,
        selectNextNote,
        selectPreviousNote,
        extendSelectionByNote,
        extendSelectionByMeasure,
        extendSelectionByPart,
        applyDragSelection,
        clearSelection,
        handleContainerClick,
        handleContainerContextMenu,
        openChordEditor,
        openKeySignatureEditor,
        openTimeSignatureEditor,
        openBassStyleEditor,
        openStructureEditor,
        openMeasuresEditor,
        handleContainerHover,
        handleContainerMouseLeave,
        restoreSelectionHighlight,
        hoveredNote,
        hoverX,
        hoverY,
        clearHover,
    } as const;
}

export type ScoreSelection = ReturnType<typeof useScoreSelection>;
